import React, { useEffect, useState } from "react";
import { X, Download, Edit2 } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import toast from "react-hot-toast";
import { Config } from "../API/config";

interface User {
    _id: string;
    name: string;
    email: string;
    phone: string;
    gender: "male" | "female";
    city: string;
    government: string;
    block: boolean;
    createdAt: string;
}

interface UserDetailsPanelProps {
    selectedUser: User;
    setSelectedUser: React.Dispatch<React.SetStateAction<User | null>>;
    users: User[];
    setUsers: React.Dispatch<React.SetStateAction<User[]>>;
    setFilteredUsers: React.Dispatch<React.SetStateAction<User[]>>;
    handleDownloadTraineeEnrollments: (traineeId: string) => Promise<void>;
}

const TraineeViewCard: React.FC<UserDetailsPanelProps> = ({
    selectedUser,
    setSelectedUser,
    users,
    setUsers,
    setFilteredUsers,
    handleDownloadTraineeEnrollments,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({
        name: "",
        email: "",
        phone: "",
        gender: selectedUser.gender,
        city: selectedUser.city,
        government: selectedUser.government,
    });
    
    useEffect(() => {
        if (selectedUser) {
            setEditForm({
                name: selectedUser.name,
                email: selectedUser.email,
                phone: selectedUser.phone,
                gender: selectedUser.gender,
                government: selectedUser.government,
                city: selectedUser.city,
            });
        }
    }, [selectedUser]);

    const handleEditUser = async () => {
        try {
            
            await axios.patch(
                `${Config.UpdateTrainee}/${selectedUser._id}`,
                editForm,
                { withCredentials: true }
            );
            toast.success("تم تحديث بيانات المستخدم بنجاح");
            setUsers(users.map(user =>
                user._id === selectedUser._id
                    ? { ...user, ...editForm }
                    : user
            ));
            setFilteredUsers(users.map(user =>
                user._id === selectedUser._id
                    ? { ...user, ...editForm }
                    : user
            ));
            setSelectedUser({ ...selectedUser, ...editForm });
            setIsEditing(false);
        } catch (err) {
            console.error("Error updating user:", err);
            toast.error("فشل في تحديث بيانات المستخدم");
        }
    };

    return (
        <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ duration: 0.5 }}
            className="fixed top-0 left-0 w-96 h-full bg-gray-50 shadow-xl p-6 overflow-auto z-50"
        >
            <button
                className="absolute top-4 left-4 bg-gray-200 hover:bg-gray-300 rounded-full p-1 transition-colors duration-200"
                onClick={() => {
                    setSelectedUser(null);
                    setIsEditing(false);
                }}
            >
                <X size={20} className="text-gray-600" />
            </button>

            <div className="mb-6 text-right">
                <h2 className="text-2xl font-bold text-gray-800 mb-1">تفاصيل المستخدم</h2>
                <div className="w-20 h-1 bg-blue-500 rounded-full"></div>
            </div>

            <div className="space-y-4 text-right">
                {isEditing ? (
                    <>
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500 font-medium">الاسم</p>
                            <input
                                type="text"
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                className="w-full mt-1 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                            />
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500 font-medium">الإيميل</p>
                            <input
                                type="email"
                                value={editForm.email}
                                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                className="w-full mt-1 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                            />
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500 font-medium">الهاتف</p>
                            <input
                                type="text"
                                value={editForm.phone}
                                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                                className="w-full mt-1 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                            />
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500 font-medium">الجنس</p>
                            <select
                                value={editForm.gender}
                                onChange={(e) => setEditForm({ ...editForm, gender: e.target.value as "male" | "female" })}
                                className="w-full mt-1 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                            >
                                <option value="male">ذكر</option>
                                <option value="female">أنثى</option>
                            </select>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <button
                                onClick={handleEditUser}
                                className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
                            >
                                حفظ التغييرات
                            </button>
                            <button
                                onClick={() => setIsEditing(false)}
                                className="w-full mt-2 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
                            >
                                إلغاء
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500 font-medium">الاسم</p>
                            <p className="text-lg text-gray-800 mt-1">{selectedUser.name}</p>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500 font-medium">الإيميل</p>
                            <p className="text-lg text-gray-800 mt-1">{selectedUser.email}</p>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500 font-medium">الهاتف</p>
                            <p className="text-lg text-gray-800 mt-1">{selectedUser.phone}</p>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500 font-medium">الجنس</p>
                            <p className="text-lg text-gray-800 mt-1">
                                {selectedUser.gender === "male" ? "ذكر" : "أنثى"}
                            </p>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <p className="text-sm text-gray-500 font-medium">تاريخ التسجيل</p>
                            <p className="text-lg text-gray-800 mt-1">
                                {new Date(selectedUser.createdAt).toLocaleDateString("ar-EG", {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </p>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow-sm">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center justify-center gap-2"
                            >
                                <Edit2 size={18} />
                                تعديل المستخدم
                            </button>
                            <button
                                onClick={() => handleDownloadTraineeEnrollments(selectedUser._id)}
                                className="w-full mt-2 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center justify-center gap-2"
                            >
                                <Download size={18} />
                                تحميل الاشتراكات
                            </button>
                        </div>
                    </>
                )}
            </div>
        </motion.div>
    );
};

export default TraineeViewCard;